/**
 * Lista das frentes de obra (#365, D10): a faixa "Precisam de atenção" por motivo, filtros em
 * linha na URL (Empresa, Obra dependente dela, Situação) e a situação de cada frente. A situação
 * e os dias de atraso vêm do servidor; o navegador só os escreve.
 *
 * Alvo visual: docs/superpowers/specs/2026-10-16-365-frentes-obra-mockup.html (tela 1).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import {
  DataTable,
  InlineFilterBar,
  InlineSelect,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components';
import { ActionButton } from '../../components/common/ActionButton';
import type { ActiveChip } from '../../hooks/useFilterState';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useControle } from '../../authorization/useVeredito';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { companyService } from '../../services/companyService';
import { workFrontService, type WorkFrontFilters } from '../../services/workFrontService';
import { ApiRequestError } from '../../types/common';
import type {
  WorkFrontListItem,
  WorkFrontObra,
  WorkFrontReason,
  WorkFrontSummary,
} from '../../types/workFront';
import { formatIsoDate } from '../fire-equipments/fireDates';
import FrenteSituacaoChip from './components/FrenteSituacaoChip';

const TOUCH_TARGET = 44;
const ROWS_PER_PAGE_DEFAULT = 10;

type StatusFilter = 'CLOSED' | 'ALL';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'CLOSED', label: 'Encerradas' },
  { value: 'ALL', label: 'Todas' },
];

/** A ordem da faixa de atenção, o texto de cada botão e o motivo que ele filtra. */
const ATTENTION_BUTTONS: readonly { reason: WorkFrontReason; text: (count: number) => string }[] = [
  { reason: 'ETAPA_ATRASADA', text: (count) => `${count} com etapa atrasada` },
  { reason: 'SEM_RESPONSAVEL', text: (count) => `${count} sem responsável` },
  { reason: 'SEM_RISCO_MARCADO', text: (count) => `${count} sem risco marcado` },
];

const NO_OBRA_HINT = 'Marque uma unidade como obra primeiro';

/** Id vindo da URL: só dígitos valem; qualquer outra coisa é como se não estivesse lá. */
const onlyDigits = (value: string | null): string => (value && /^\d+$/.test(value) ? value : '');

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

const isForbidden = (err: unknown): boolean => err instanceof ApiRequestError && err.status === 403;

const SmallText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" color="text.secondary">
    {children}
  </Typography>
);

const CurrentStageCell: React.FC<{ front: WorkFrontListItem }> = ({ front }) => {
  const { kind, name, plannedEnd } = front.currentStage;
  if (kind === 'NONE') return <SmallText>Sem etapas</SmallText>;
  if (kind === 'DONE') return <Typography variant="body1">Todas concluídas</Typography>;

  const until = plannedEnd ? formatIsoDate(plannedEnd) : null;
  const hint =
    kind === 'NEXT'
      ? `próxima${until ? `, até ${until}` : ''}`
      : until
        ? `${front.situation === 'ETAPA_ATRASADA' ? 'previsto até' : 'até'} ${until}`
        : undefined;
  return (
    <Box>
      <Typography variant="body1">{name}</Typography>
      {hint && <SmallText>{hint}</SmallText>}
    </Box>
  );
};

const PENDENCY_TEXT: Partial<Record<WorkFrontReason, string>> = {
  SEM_RESPONSAVEL: 'sem responsável',
  SEM_RISCO_MARCADO: 'sem risco marcado',
};

const SituationCell: React.FC<{ front: WorkFrontListItem }> = ({ front }) => {
  const pendencies = front.reasons.flatMap((reason) => PENDENCY_TEXT[reason] ?? []);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
      <FrenteSituacaoChip situation={front.situation} lateDays={front.lateDays} />
      {front.situation === 'COM_PENDENCIA' && pendencies.length > 0 && <SmallText>{pendencies.join(' e ')}</SmallText>}
    </Box>
  );
};

const buildColumns = (showCompany: boolean): DataTableColumn<WorkFrontListItem>[] => [
  {
    id: 'name',
    label: 'Frente',
    primary: true,
    render: (front) => (
      <Box>
        <Typography variant="body1" fontWeight="bold">
          {front.name}
        </Typography>
        <SmallText>{showCompany && front.company ? `${front.unit.name} · ${front.company.name}` : front.unit.name}</SmallText>
      </Box>
    ),
  },
  { id: 'responsible', label: 'Responsável', render: (front) => front.responsible?.name ?? '—' },
  { id: 'stage', label: 'Etapa atual', render: (front) => <CurrentStageCell front={front} /> },
  { id: 'workers', label: 'Trabalhadores', align: 'right', hideOnMobile: true, render: (front) => front.activeWorkersCount },
  { id: 'risks', label: 'Riscos', align: 'right', hideOnMobile: true, render: (front) => front.markedRisksCount },
  { id: 'situation', label: 'Situação', render: (front) => <SituationCell front={front} /> },
  {
    id: 'actions',
    label: 'Ações',
    render: (front) => (
      // O clique na ação é da ação: sem isto ele sobe até a linha e navega duas vezes.
      <Box sx={{ display: 'flex', gap: 1 }} onClick={(event) => event.stopPropagation()}>
        <RouteActionButton
          para={`/work-fronts/${front.id}`}
          exige={pode('work_front.read')}
          variant="outlined"
          aria-label={`Abrir ${front.name}`}
          sx={{ minHeight: TOUCH_TARGET }}
        >
          Abrir
        </RouteActionButton>
        <RouteActionButton
          para={`/work-fronts/${front.id}/edit`}
          exige={pode('work_front.update')}
          aria-label={`Editar ${front.name}`}
          sx={{ minHeight: TOUCH_TARGET }}
        >
          Editar
        </RouteActionButton>
      </Box>
    ),
  },
];

export const WorkFrontListPage: React.FC = () => {
  const navigate = useNavigate();
  const { shouldShowCompanyField } = useCompanyContext();
  const criar = useControle('/work-fronts/new', pode('work_front.create'), 'Criar a primeira frente');
  const marcarObra = useControle('/units', pode('unit.update'), 'Marcar uma unidade como obra');

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_DEFAULT);
  const [obras, setObras] = useState<WorkFrontObra[] | null>(null);
  const [rows, setRows] = useState<WorkFrontListItem[]>([]);
  const [summary, setSummary] = useState<WorkFrontSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const companyId = shouldShowCompanyField ? onlyDigits(searchParams.get('companyId')) : '';
  const rawUnitId = onlyDigits(searchParams.get('unitId'));
  const statusParam = searchParams.get('status');
  const status: StatusFilter | '' = STATUS_OPTIONS.find((option) => option.value === statusParam)?.value ?? '';
  const reasonParam = searchParams.get('atencao');
  const reason = ATTENTION_BUTTONS.find((button) => button.reason === reasonParam)?.reason ?? null;

  // Obra que não está entre as do escopo (link antigo, empresa trocada) é como se não estivesse lá.
  const unitId = obras !== null && !obras.some((obra) => String(obra.id) === rawUnitId) ? '' : rawUnitId;

  const loadCompanyOptions = useCallback(async (): Promise<readonly { value: string; label: string }[]> => {
    const companies = await companyService.getCompaniesForDropdown();
    return companies.map((company) => ({ value: String(company.id), label: company.name }));
  }, []);
  const companyList = useListaPermitida('company.read', 'Empresas', loadCompanyOptions, {
    habilitado: shouldShowCompanyField,
  });

  const fail = useCallback((err: unknown, fallback: string) => {
    if (isForbidden(err)) setForbidden(true);
    else setError(errorMessage(err, fallback));
    setLoading(false);
  }, []);

  useEffect(() => {
    let alive = true;
    setObras(null);
    workFrontService
      .obras(companyId || undefined)
      .then((response) => alive && setObras(response))
      .catch((err: unknown) => alive && fail(err, 'Erro ao carregar as obras'));
    return () => {
      alive = false;
    };
  }, [companyId, reload, fail]);

  useEffect(() => {
    if (obras === null) return;
    let alive = true;
    setSummaryError(null);
    const filters: WorkFrontFilters = {};
    if (companyId) filters.companyId = companyId;
    if (unitId) filters.unitId = unitId;
    workFrontService
      .summary(filters)
      .then((response) => alive && setSummary(response))
      .catch((err: unknown) => {
        if (!alive) return;
        setSummary(null);
        setSummaryError(errorMessage(err, 'Erro ao carregar o resumo'));
      });
    return () => {
      alive = false;
    };
  }, [obras, companyId, unitId, reload]);

  useEffect(() => {
    if (obras === null) return;
    let alive = true;
    const filters: WorkFrontFilters = {};
    if (companyId) filters.companyId = companyId;
    if (unitId) filters.unitId = unitId;
    if (status === 'CLOSED') filters.status = 'CLOSED';
    if (status === '') filters.status = 'ACTIVE';
    if (search) filters.search = search;

    setLoading(true);
    setError(null);
    workFrontService
      .list(filters)
      .then((response) => {
        if (!alive) return;
        setRows(response);
        setLoading(false);
      })
      .catch((err: unknown) => alive && fail(err, 'Erro ao carregar as frentes'));
    return () => {
      alive = false;
    };
  }, [obras, companyId, unitId, status, search, reload, fail]);

  const retry = () => setReload((count) => count + 1);

  /** Grava filtros na URL (vazio apaga a chave) e volta à primeira página. */
  const setUrlFilters = useCallback(
    (changes: Partial<Record<'companyId' | 'unitId' | 'status' | 'atencao', string>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true }
      );
      setPage(0);
    },
    [setSearchParams]
  );

  const clearAll = () => {
    setSearchParams({}, { replace: true });
    setSearch('');
    setPage(0);
  };

  const toggleAttention = (target: WorkFrontReason) => setUrlFilters({ atencao: reason === target ? '' : target });

  const visibleRows = reason ? rows.filter((front) => front.reasons.includes(reason)) : rows;
  const pageRows = visibleRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Empresa e obra são o escopo da lista, não um recorte dela: escolher só a obra e não achar
  // frente é "a obra não tem frentes", e não "os filtros não acharam".
  const filtersActive = Boolean(status || reason || search);
  const hasAttention = summary !== null && ATTENTION_BUTTONS.some(({ reason: key }) => summary.byReason[key] > 0);
  const withoutObra = obras !== null && obras.length === 0;

  const obraOptions = (obras ?? []).map((obra) => ({ value: String(obra.id), label: obra.name }));
  const optionLabel = (options: readonly { value: string; label: string }[], value: string) =>
    options.find((option) => option.value === value)?.label ?? value;
  const chosenObra = obras?.find((obra) => String(obra.id) === unitId);

  const chips: ActiveChip[] = [];
  if (companyId) {
    chips.push({
      key: 'companyId',
      label: 'Empresa',
      value: optionLabel(companyList.itens, companyId),
      onDelete: () => setUrlFilters({ companyId: '', unitId: '' }),
    });
  }
  if (unitId) {
    chips.push({
      key: 'unitId',
      label: 'Obra',
      value: optionLabel(obraOptions, unitId),
      onDelete: () => setUrlFilters({ unitId: '' }),
    });
  }
  if (status) {
    chips.push({
      key: 'status',
      label: 'Situação',
      value: optionLabel(STATUS_OPTIONS, status),
      onDelete: () => setUrlFilters({ status: '' }),
    });
  }

  const limparFiltros = {
    label: 'Limpar filtros',
    node: 'work-fronts',
    deniedHint: 'Limpe os filtros para ver as frentes.',
    onClick: clearAll,
    allowed: true,
  };

  // Um objeto com texto literal por situação: o teste dos textos de vazio lê cada description
  // como texto inteiro, então a escolha entre as frases fica nos ternários de fora.
  const noObraAction = {
    label: 'Marcar uma unidade como obra',
    node: 'units',
    deniedHint: 'Peça a quem administra a empresa para marcar uma unidade como obra.',
    onClick: () => navigate('/units'),
    allowed: !marcarObra.disabled,
  };
  const firstFrontAction = {
    label: 'Criar a primeira frente',
    node: 'work-fronts',
    deniedHint: 'Peça a quem administra a empresa para criar a primeira frente.',
    onClick: () => navigate(unitId ? `/work-fronts/new?unitId=${unitId}` : '/work-fronts/new'),
    allowed: !criar.disabled,
  };
  const scopeIsCompany = !shouldShowCompanyField || companyId !== '';

  const emptyState = withoutObra
    ? scopeIsCompany
      ? {
          title: 'Nenhuma unidade desta empresa está marcada como obra',
          description:
            'As frentes ficam dentro de uma obra, e a obra é uma unidade da empresa. Marque a unidade onde o trabalho acontece e volte aqui para criar a primeira frente.',
          action: noObraAction,
        }
      : {
          title: 'Nenhuma unidade do seu acesso está marcada como obra',
          description:
            'As frentes ficam dentro de uma obra, e a obra é uma unidade da empresa. Marque a unidade onde o trabalho acontece e volte aqui para criar a primeira frente.',
          action: noObraAction,
        }
    : filtersActive && summary
      ? {
          title: 'Nenhuma frente com esses filtros',
          description: `Há ${summary.total} ${summary.total === 1 ? 'frente' : 'frentes'} ${
            chosenObra ? 'nesta obra' : scopeIsCompany ? 'para esta empresa' : 'no seu acesso'
          }, mas nenhuma atende ao que você escolheu.`,
          action: limparFiltros,
        }
      : filtersActive
        ? {
            title: 'Nenhuma frente com esses filtros',
            description: 'Nenhuma frente atende ao que você escolheu.',
            action: limparFiltros,
          }
        : chosenObra
          ? {
              title: `${chosenObra.name} ainda não tem frentes`,
              description:
                'Uma frente é uma parte da obra com equipe e prazo próprios, como a fundação ou a estrutura. Crie a primeira; depois cadastre as etapas, marque os riscos e vincule os trabalhadores. Antes de vincular trabalhadores, confira que a obra tem setores ligados a ela.',
              action: firstFrontAction,
            }
          : {
              title: 'As obras desta empresa ainda não têm frentes',
              description:
                'Uma frente é uma parte da obra com equipe e prazo próprios, como a fundação ou a estrutura. Crie a primeira; depois cadastre as etapas, marque os riscos e vincule os trabalhadores. Antes de vincular trabalhadores, confira que a obra tem setores ligados a ela.',
              action: firstFrontAction,
            };

  const newFrontButton = withoutObra ? (
    <Tooltip title={NO_OBRA_HINT}>
      <span>
        <ActionButton
          acao="work_front.create"
          rotulo="Nova frente"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disabled
        >
          Nova frente
        </ActionButton>
      </span>
    </Tooltip>
  ) : (
    <RouteActionButton
      para="/work-fronts/new"
      exige={pode('work_front.create')}
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
    >
      Nova frente
    </RouteActionButton>
  );

  const breadcrumbs = [{ label: 'Início', path: '/' }, { label: 'Frentes de obra' }];

  if (forbidden) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Frentes de obra">
        <Alert severity="info">Seu perfil não inclui o acesso às frentes de obra. Peça a quem administra a empresa.</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Frentes de obra"
      subtitle="Cada frente de uma obra, com etapas, riscos e trabalhadores."
      actions={newFrontButton}
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={retry} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!error && summaryError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={retry} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar a faixa "Precisam de atenção". A lista abaixo continua valendo.
        </Alert>
      )}

      {!error && !summaryError && summary === null && (
        <Skeleton variant="rounded" height={TOUCH_TARGET} sx={{ mb: 3 }} />
      )}

      {!error && summary !== null && summary.total > 0 && (
        <Box
          role="group"
          aria-labelledby="faixa-atencao"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}
        >
          <Typography id="faixa-atencao" variant="body1" color="text.secondary" sx={{ mr: 1 }}>
            Precisam de atenção
          </Typography>
          {hasAttention ? (
            ATTENTION_BUTTONS.map(({ reason: key, text }) => (
              <Button
                key={key}
                variant={reason === key ? 'contained' : 'outlined'}
                color="inherit"
                aria-pressed={reason === key}
                onClick={() => toggleAttention(key)}
                sx={{ minHeight: TOUCH_TARGET, fontWeight: 600 }}
              >
                {text(summary.byReason[key])}
              </Button>
            ))
          ) : (
            <Typography variant="body1">Nenhuma frente precisa de atenção agora</Typography>
          )}
        </Box>
      )}

      {obras !== null && (
        <Box sx={{ mb: 3 }}>
          <InlineFilterBar
            searchPlaceholder="Nome da frente ou responsável"
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(0);
            }}
            resultCount={error ? undefined : visibleRows.length}
            resultNoun="frentes"
            chips={chips}
            onClearAll={clearAll}
            loading={loading}
          >
            {shouldShowCompanyField && (
              <Tooltip title={companyList.bloqueio ?? ''}>
                <span>
                  <InlineSelect
                    label="Empresa"
                    value={companyId}
                    options={[...companyList.itens]}
                    onChange={(value) => setUrlFilters({ companyId: value, unitId: '' })}
                    emptyLabel="Todas as empresas"
                    minWidth={200}
                    disabled={!companyList.permitido}
                  />
                </span>
              </Tooltip>
            )}
            <InlineSelect
              label="Obra"
              value={unitId}
              options={obraOptions}
              onChange={(value) => setUrlFilters({ unitId: value })}
              emptyLabel="Todas as obras"
              minWidth={200}
            />
            <InlineSelect
              label="Situação"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(value) => setUrlFilters({ status: value })}
              emptyLabel="Em andamento"
              minWidth={180}
            />
          </InlineFilterBar>
        </Box>
      )}

      {!error && (
        <DataTable
          columns={buildColumns(shouldShowCompanyField && companyId === '')}
          rows={pageRows}
          getRowId={(front) => front.id}
          loading={loading || obras === null}
          emptyState={emptyState}
          onRowClick={(front) => navigate(`/work-fronts/${front.id}`)}
          pagination={{
            page,
            rowsPerPage,
            totalCount: visibleRows.length,
            onPageChange: setPage,
            onRowsPerPageChange: (value) => {
              setRowsPerPage(value);
              setPage(0);
            },
          }}
        />
      )}
    </PageShell>
  );
};

export default WorkFrontListPage;
